import { useUpdateStatus, updateLabel } from '../../hooks/useUpdateStatus';

/**
 * UpdateStatusRow — live update status in the sidebar footer.
 *
 * Idle  → green dot + "Up to date" + mono version.
 * Ready → amber row + "Restart" button calling install().
 * Web   → version is '' → renders null (desktop-only widget).
 *
 * Design source: apps/pdf-suite/docs/redesign/desktop-shell-mockup.html .update-row
 */
export default function UpdateStatusRow() {
  const { version, state, install } = useUpdateStatus();
  const { text, tone } = updateLabel(state, version);

  // Only show in the desktop app (Electron exposes version via IPC)
  if (!version) return null;

  if (tone === 'action' && state === 'ready') {
    return (
      <button
        onClick={install}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '9px',
          padding: '7px 12px',
          borderRadius: '9px',
          cursor: 'pointer',
          fontSize: '12.5px',
          color: 'var(--ds-amber, #92400e)',
          background: 'var(--ds-amber-bg, #FEF4E6)',
          border: '1px solid var(--ds-amber-line, #FAD9A6)',
          width: '100%',
          textAlign: 'left',
          fontFamily: 'inherit',
        }}
      >
        <span
          style={{
            width: '7px',
            height: '7px',
            borderRadius: '50%',
            background: 'var(--ds-amber-bright, #F59E0B)',
            flexShrink: 0,
            boxShadow: '0 0 0 3px rgba(var(--ds-amber-bright-rgb, 245,158,11), .18)',
            display: 'inline-block',
          }}
        />
        {text}
        <span
          className="mono"
          style={{ marginLeft: 'auto', color: 'var(--ds-ink-3)', fontSize: '12px' }}
        >
          {version}
        </span>
      </button>
    );
  }

  // idle / available / downloading — all show the same green-dot row
  // (available/downloading states also show here until the download completes)
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '9px',
        padding: '7px 12px',
        borderRadius: '9px',
        cursor: 'default',
        fontSize: '12.5px',
        color: 'var(--ds-ink-2)',
      }}
    >
      <span
        style={{
          width: '7px',
          height: '7px',
          borderRadius: '50%',
          background: tone === 'busy' ? 'var(--ds-amber-bright, #F59E0B)' : 'var(--ds-green-bright, #22C55E)',
          flexShrink: 0,
          boxShadow: tone === 'busy'
            ? '0 0 0 3px rgba(var(--ds-amber-bright-rgb, 245,158,11), .18)'
            : '0 0 0 3px rgba(var(--ds-green-bright-rgb, 34,197,94), .16)',
          display: 'inline-block',
        }}
      />
      {text}
      <span
        className="mono"
        style={{ marginLeft: 'auto', color: 'var(--ds-ink-3)', fontSize: '12px' }}
      >
        {version}
      </span>
    </div>
  );
}
